import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { MetaTags } from '@/components/MetaTags';
import { Download, MessageCircle, Smartphone, AlertCircle, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Capacitor } from '@capacitor/core';
import WhatsAppStickers from '@/plugins/WhatsAppStickers';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StickersPage() {
  const [isNative, setIsNative] = useState(false);
  const [whatsAppInstalled, setWhatsAppInstalled] = useState(false);
  const [addingPack, setAddingPack] = useState(null);
  const [addedPacks, setAddedPacks] = useState(new Set());
  const [stickerPacks, setStickerPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if running in native app
    setIsNative(Capacitor.isNativePlatform());
    
    // Check if WhatsApp is installed
    const checkWhatsApp = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const { installed } = await WhatsAppStickers.isWhatsAppInstalled();
          setWhatsAppInstalled(installed);
        } catch (e) {
          console.log('Could not check WhatsApp status');
        }
      }
    };
    
    // Fetch sticker packs from API
    const fetchPacks = async () => {
      try {
        const res = await axios.get(`${API}/stickers/packs`);
        setStickerPacks(res.data);
      } catch (error) {
        console.error('Error fetching sticker packs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    checkWhatsApp();
    fetchPacks();
  }, []);

  const handleAddToWhatsApp = async (packId: string, packName: string) => {
    if (!isNative) {
      alert('Los stickers solo se pueden agregar desde la app de Darkar Company');
      return;
    }
    
    if (!whatsAppInstalled) {
      alert('Necesitas tener WhatsApp instalado');
      return;
    }

    setAddingPack(packId);
    
    try {
      await WhatsAppStickers.addStickerPack({ packId, packName });
      setAddedPacks(prev => new Set([...prev, packId]));
    } catch (error) {
      console.error('Error adding sticker pack:', error);
      alert('No se pudo agregar el paquete. Inténtalo de nuevo.');
    } finally {
      setAddingPack(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-black">
      <MetaTags 
        title="Stickers para WhatsApp | Darkar Company"
        description="Descarga los stickers oficiales de Darkar Company para WhatsApp"
      />
      <Navbar />
      
      <main className="flex-1 pt-20">
        <div className="max-w-4xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-full text-sm font-medium mb-4">
              <MessageCircle size={16} />
              WhatsApp Stickers
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: 'SupersonicRocketship, sans-serif' }}>
              STICKERS OFICIALES
            </h1>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Descarga los paquetes de stickers de tus personajes favoritos y úsalos en WhatsApp
            </p>
          </div>

          {/* Platform Notice */}
          {!isNative && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 mb-8 flex items-start gap-4">
              <Smartphone className="text-yellow-500 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-yellow-500 font-bold mb-1">Disponible en la App</h3>
                <p className="text-yellow-500/80 text-sm">
                  Para agregar stickers a WhatsApp, descarga la app de Darkar Company desde la App Store o Google Play.
                </p>
                <div className="flex gap-3 mt-4">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.darkarcompany.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-black border border-white/20 rounded-lg text-white text-sm hover:bg-white/10 transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                    </svg>
                    Google Play
                  </a>
                  <a 
                    href="https://apps.apple.com/app/darkar-company/id0000000000"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-black border border-white/20 rounded-lg text-white text-sm hover:bg-white/10 transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    App Store
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* WhatsApp Not Installed Warning */}
          {isNative && !whatsAppInstalled && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-8 flex items-start gap-4">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-red-500 font-bold mb-1">WhatsApp no detectado</h3>
                <p className="text-red-500/80 text-sm">
                  Necesitas tener WhatsApp instalado para agregar los stickers.
                </p>
              </div>
            </div>
          )}

          {/* Sticker Packs */}
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-gray-400" size={32} />
              </div>
            ) : stickerPacks.length === 0 ? (
              <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
                <MessageCircle className="mx-auto text-gray-500 mb-4" size={48} />
                <p className="text-gray-400">No hay paquetes de stickers disponibles</p>
                <p className="text-gray-500 text-sm mt-1">Vuelve pronto para ver nuevos stickers</p>
              </div>
            ) : (
              stickerPacks.map((pack) => (
                <div 
                  key={pack.id}
                  className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        {/* Tray Icon */}
                        <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {pack.tray_image_url ? (
                            <img src={pack.tray_image_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <MessageCircle className="text-green-500" size={32} />
                          )}
                        </div>
                        
                        {/* Pack Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl font-bold text-white mb-1">{pack.name}</h3>
                          <p className="text-gray-400 text-sm mb-2">{pack.publisher}</p>
                          <span className="text-xs text-gray-500">{pack.stickers?.length || 0} stickers</span>
                        </div>
                      </div>

                      {/* Add Button - Full width on mobile */}
                      <Button
                        onClick={() => handleAddToWhatsApp(pack.identifier, pack.name)}
                        disabled={!isNative || !whatsAppInstalled || addingPack === pack.id || addedPacks.has(pack.id)}
                        className={`w-full sm:w-auto flex-shrink-0 ${
                          addedPacks.has(pack.id) 
                            ? 'bg-green-600 hover:bg-green-600' 
                            : 'bg-green-500 hover:bg-green-600'
                        } text-white rounded-full px-6`}
                      >
                        {addedPacks.has(pack.id) ? (
                          <>
                            <Check size={16} className="mr-2" />
                            Agregado
                          </>
                        ) : addingPack === pack.id ? (
                          'Agregando...'
                        ) : (
                          <>
                            <Download size={16} className="mr-2" />
                            Agregar
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Preview Grid */}
                  {pack.stickers?.length > 0 && (
                    <div className="border-t border-white/10 p-4 bg-black/30">
                      <p className="text-xs text-gray-500 mb-3">Vista previa</p>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {pack.stickers.slice(0, 8).map((sticker, i) => (
                          <div 
                            key={sticker.id || i}
                            className="w-16 h-16 bg-white/10 rounded-lg flex-shrink-0 overflow-hidden"
                          >
                            <img 
                              src={sticker.image_url} 
                              alt=""
                              className="w-full h-full object-contain p-1"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Info Section */}
          <div className="mt-12 text-center">
            <h3 className="text-white font-bold mb-2">¿Cómo funcionan los stickers?</h3>
            <p className="text-gray-400 text-sm max-w-lg mx-auto">
              Los stickers se agregan directamente a tu WhatsApp. Una vez agregados, 
              podrás encontrarlos en la sección de stickers de cualquier conversación.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
